import { Link, NavLink } from "react-router-dom";
import { useState } from "react";

/* ================= ICONS ================= */
function IconMenu({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
function IconX({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <path d="M6 6l12 12M18 6l-12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
function IconPhone({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <path
        d="M6.5 3.5h3l1.2 5-2 1.3c1.2 2.2 3 4 5.2 5.2l1.3-2 5 1.2v3c0 1-1 2-2.2 2C11.2 21.2 2.8 12.8 3 5.7 3 4.5 4 3.5 5 3.5h1.5Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function IconWhatsapp({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <path
        d="M20 11.9a8 8 0 0 1-11.7 7L4 20l1.2-3.9A8 8 0 1 1 20 11.9Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M9.2 9.1c.2-.5.4-.6.8-.6h.7c.2 0 .5.1.6.4l.8 1.9c.1.3.1.6-.1.8l-.5.6c-.2.2-.2.5 0 .7.6 1 1.5 1.8 2.5 2.4.2.1.5.1.7-.1l.6-.5c.2-.2.5-.2.8-.1l1.9.8c.3.1.4.4.4.6v.7c0 .4-.1.6-.6.8-.5.2-1.7.6-3.6-.1-1.9-.7-4.5-2.9-5.6-5.2-1.1-2.3-.4-3.7-.1-4.1Z"
        fill="currentColor"
        opacity=".9"
      />
    </svg>
  );
}

/* ================= NAV ITEM ================= */
function NavItem({ to, children, onClick }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `px-3 py-2 rounded-xl text-sm font-bold transition ${
          isActive
            ? "bg-slate-100 text-slate-900"
            : "text-slate-700 hover:bg-slate-100 hover:text-slate-900"
        }`
      }
    >
      {children}
    </NavLink>
  );
}

/* ================= NAVBAR ================= */
export default function Navbar() {
  const [open, setOpen] = useState(false);

  /* ✅ FIXED NUMBER */
  const phoneRaw = "+65 8530 3990";
  const tel = "tel:+6585303990";
  const wa = "https://wa.me/6585303990";

  return (
    <header className="sticky top-0 z-50">
      {/* White Top Bar */}
      <div className="bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">

          {/* LOGO ONLY */}
          <Link to="/" className="flex items-center">
            <div className="rounded-xl bg-slate-50 border border-slate-200 px-2 py-1 shadow-sm">
              <img
                src="/images/company-logo.png"
                alt="Company logo"
                className="object-contain h-8 sm:h-10 md:h-12 lg:h-14 w-auto"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-2">
            <NavItem to="/">Home</NavItem>
            <NavItem to="/services">Services</NavItem>
            <NavItem to="/projects">Projects</NavItem>
            <NavItem to="/contact">Contact</NavItem>
          </nav>

          {/* Right CTA */}
          <div className="hidden md:flex items-center gap-2">
            <a
              href={wa}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold bg-emerald-600 text-white hover:bg-emerald-700 transition shadow-sm"
            >
              <IconWhatsapp />
              WhatsApp
            </a>

            <a
              href={tel}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold border border-slate-300 text-slate-900 hover:bg-slate-100 transition"
            >
              <IconPhone />
              {phoneRaw}
            </a>
          </div>

          {/* Mobile Button */}
          <button
            onClick={() => setOpen((v) => !v)}
            className="md:hidden inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-300 text-sm font-bold text-slate-900"
          >
            {open ? <IconX /> : <IconMenu />}
            {open ? "Close" : "Menu"}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      {open && (
        <div className="md:hidden bg-white border-b border-slate-200">
          <div className="max-w-6xl mx-auto px-4 py-4 flex flex-col gap-2">
            <NavItem to="/" onClick={() => setOpen(false)}>Home</NavItem>
            <NavItem to="/services" onClick={() => setOpen(false)}>Services</NavItem>
            <NavItem to="/projects" onClick={() => setOpen(false)}>Projects</NavItem>
            <NavItem to="/contact" onClick={() => setOpen(false)}>Contact</NavItem>

            <div className="pt-3 grid grid-cols-1 gap-2">
              <a
                href={wa}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl font-bold bg-emerald-600 text-white"
              >
                <IconWhatsapp />
                WhatsApp
              </a>

              <a
                href={tel}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl font-bold border border-slate-300 text-slate-900"
              >
                <IconPhone />
                Call {phoneRaw}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
